import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Scale, Paperclip, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES, CURRENCIES, TENDER_TYPES, CATEGORIES, UNIT_TYPES, EVALUATION_METHODS } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TOTAL = 50 * 1024 * 1024;
const fmtSize = (b) => (b === 0 ? "0 KB" : b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const Field = ({ label, children, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">{label}</Label>
    {children}
  </div>
);

export default function CreateTender() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [rulesFiles, setRulesFiles] = useState([]);
  const [form, setForm] = useState({
    title: "", tender_type: "RFP", category: "Goods", description: "", country: "",
    organization: "", unit_type: "Operative Unit", estimated_value: "", currency: "USD",
    submission_deadline: "", evaluation_method: "LESRB",
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const totalSize = [...files, ...rulesFiles].reduce((s, f) => s + f.size, 0);

  const addFiles = (setter, current) => (e) => {
    const picked = Array.from(e.target.files || []);
    const otherSize = totalSize - current.reduce((s, f) => s + f.size, 0);
    const next = [...current, ...picked];
    if (otherSize + next.reduce((s, f) => s + f.size, 0) > MAX_TOTAL) {
      toast.error("Total attached files must not exceed 50 MB.");
    } else {
      setter(next);
    }
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.country || !form.organization || !form.description || !form.estimated_value || !form.submission_deadline) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/tenders`, { ...form, estimated_value: parseFloat(form.estimated_value) });
      const uploads = [...files.map((f) => [f, "reference"]), ...rulesFiles.map((f) => [f, "rules"])];
      for (const [f, category] of uploads) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", category);
        try {
          await axios.post(`${API}/tenders/${data.id}/attachments`, fd);
        } catch (err) {
          toast.error(`${f.name}: ${err.response?.data?.detail || "upload failed"}`);
        }
      }
      const n = uploads.length;
      toast.success(`Tender ${data.reference_number} created as draft${n ? ` with ${n} attachment${n > 1 ? "s" : ""}` : ""}.`);
      navigate(`/tenders/${data.id}`);
    } catch {
      toast.error("Failed to create tender.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "bg-white border-slate-300 focus-visible:ring-[#0050A0]";

  return (
    <div className="max-w-5xl">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-[#0050A0] mb-3">New Procurement</p>
        <h1 className="font-serif text-4xl text-slate-900">Float a Tender</h1>
        <p className="text-sm text-slate-500 mt-3 max-w-2xl leading-relaxed">
          Define the procurement below. Once created, you can generate a legally compliant tender document
          drafted against the procurement law of the country of execution — or UN rules where none exist.
        </p>
      </div>

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-2 bg-white border border-slate-200 p-8 space-y-6">
          <Field label="Tender Title *" testId="field-title">
            <Input data-testid="input-title" className={inputCls} placeholder="e.g., Supply of 500 Electric Delivery Vans for Urban Mail Operations" value={form.title} onChange={(e) => set("title")(e.target.value)} />
          </Field>

          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Tender Type *" testId="field-tender-type">
              <Select value={form.tender_type} onValueChange={set("tender_type")}>
                <SelectTrigger data-testid="select-tender-type" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>{TENDER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Category *" testId="field-category">
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger data-testid="select-category" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Scope / Description *" testId="field-description">
            <Textarea data-testid="input-description" className={`${inputCls} min-h-[120px]`} placeholder="Describe the goods, services or works being procured, quantities, technical requirements, delivery locations…" value={form.description} onChange={(e) => set("description")(e.target.value)} />
            <div className="border border-dashed border-slate-300 p-4 mt-2" data-testid="scope-attachments-box">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Paperclip className="w-3.5 h-3.5 text-[#0050A0]" aria-hidden="true" />
                  <span>Attach reference material (PDF, DOCX, TXT, MD, CSV) — the AI reads it when drafting the tender document.</span>
                </div>
                <label className="shrink-0 cursor-pointer border border-slate-300 text-slate-600 px-3 py-1.5 text-xs font-semibold hover:bg-slate-900 hover:text-white transition-colors duration-200">
                  Browse files
                  <input type="file" multiple accept=".pdf,.docx,.txt,.md,.csv" className="hidden" onChange={addFiles(setFiles, files)} data-testid="scope-file-input" />
                </label>
              </div>
              {files.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 px-3 py-1.5" data-testid={`pending-file-${i}`}>
                      <span className="truncate text-slate-700">{f.name} <span className="font-mono text-slate-400">({fmtSize(f.size)})</span></span>
                      <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} data-testid={`remove-pending-file-${i}`} className="text-slate-400 hover:text-red-600 transition-colors duration-200 ml-3">
                        <X className="w-3.5 h-3.5" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className={`text-[11px] font-mono mt-2 ${totalSize > MAX_TOTAL ? "text-red-600" : "text-slate-400"}`} data-testid="pending-total-size">
                Total: {fmtSize(totalSize)} / 50 MB
              </p>
            </div>
            <div className="border border-[#0050A0]/40 bg-[#F0F7FC] p-4 mt-2" data-testid="rules-attachments-box">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Scale className="w-3.5 h-3.5 text-[#0050A0]" aria-hidden="true" />
                  <span>
                    <strong>Rules & Procedures (mandatory compliance)</strong> — upload documents specifying the procurement
                    process and laws the draft MUST follow. These take precedence while drafting.
                  </span>
                </div>
                <label className="shrink-0 cursor-pointer bg-[#0050A0] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#003F7A] transition-colors duration-200">
                  Upload rules
                  <input type="file" multiple accept=".pdf,.docx,.txt,.md,.csv" className="hidden" onChange={addFiles(setRulesFiles, rulesFiles)} data-testid="rules-file-input" />
                </label>
              </div>
              {rulesFiles.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {rulesFiles.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center justify-between text-xs bg-white border border-[#0050A0]/30 px-3 py-1.5" data-testid={`pending-rules-file-${i}`}>
                      <span className="truncate text-slate-700">{f.name} <span className="font-mono text-slate-400">({fmtSize(f.size)})</span></span>
                      <button type="button" onClick={() => setRulesFiles(rulesFiles.filter((_, j) => j !== i))} data-testid={`remove-pending-rules-file-${i}`} className="text-slate-400 hover:text-red-600 transition-colors duration-200 ml-3">
                        <X className="w-3.5 h-3.5" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Field>

          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Procuring Organization *" testId="field-organization">
              <Input data-testid="input-organization" className={inputCls} placeholder="e.g., India Post, Deutsche Post, USPS" value={form.organization} onChange={(e) => set("organization")(e.target.value)} />
            </Field>
            <Field label="Unit Type *" testId="field-unit-type">
              <Select value={form.unit_type} onValueChange={set("unit_type")}>
                <SelectTrigger data-testid="select-unit-type" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>{UNIT_TYPES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            <Field label="Estimated Value *" testId="field-value">
              <Input data-testid="input-estimated-value" type="number" min="0" className={`${inputCls} font-mono`} placeholder="1000000" value={form.estimated_value} onChange={(e) => set("estimated_value")(e.target.value)} />
            </Field>
            <Field label="Currency *" testId="field-currency">
              <Select value={form.currency} onValueChange={set("currency")}>
                <SelectTrigger data-testid="select-currency" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Submission Deadline *" testId="field-deadline">
              <Input data-testid="input-deadline" type="date" className={`${inputCls} font-mono`} value={form.submission_deadline} onChange={(e) => set("submission_deadline")(e.target.value)} />
            </Field>
          </div>

          <Field label="Evaluation Method *" testId="field-evaluation">
            <Select value={form.evaluation_method} onValueChange={set("evaluation_method")}>
              <SelectTrigger data-testid="select-evaluation-method" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>{EVALUATION_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>

        <div className="space-y-6">
          <div className="bg-[#00294F] p-8">
            <Scale className="w-6 h-6 text-[#009EDB] mb-4" aria-hidden="true" />
            <h3 className="font-serif text-xl text-white mb-3">Country of Execution</h3>
            <p className="text-xs text-slate-300 leading-relaxed mb-5">
              The tender document will be drafted in compliance with this country's procurement laws.
              Where no local framework exists, UNCITRAL Model Law and UN procurement rules apply.
            </p>
            <Select value={form.country} onValueChange={set("country")}>
              <SelectTrigger data-testid="select-country" className="bg-white border-0 text-slate-900">
                <SelectValue placeholder="Select country *" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.country && (
              <Link
                to={`/countries/${encodeURIComponent(form.country)}`}
                data-testid="view-country-profile-link"
                className="inline-block mt-4 text-xs text-[#7DD3F5] font-medium hover:text-white transition-colors duration-200 underline underline-offset-4"
              >
                View {form.country}'s procurement deep dive →
              </Link>
            )}
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="create-tender-submit-btn"
            className="w-full bg-[#0050A0] text-white py-4 text-sm font-semibold hover:bg-[#003F7A] transition-colors duration-200 disabled:opacity-60"
          >
            {saving ? "Creating…" : "Create Tender (Draft)"}
          </button>
        </div>
      </form>
    </div>
  );
}
